import { makeStyles, createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import { DatePicker } from '@material-ui/pickers';
import React from 'react';
import { useIntl } from 'react-intl';
import messages from './messages';

// Create a custom theme to style the expanded calendar popovers
const datePickerTheme = createMuiTheme({
  palette: {
    primary: {
      main: '#4f46e5', // indigo-600
    },
    text: {
      primary: '#1e293b', // slate-800
      secondary: '#64748b', // slate-500
    }
  },
  typography: {
    fontFamily: 'Outfit, Inter, sans-serif',
  },
  overrides: {
    MuiPickersCalendarHeader: {
      switchHeader: {
        backgroundColor: 'transparent',
      },
      transitionContainer: {
        '& p': {
          fontWeight: '700',
          fontSize: '16px',
          color: '#1e293b',
        }
      },
      dayLabel: {
        fontWeight: '600',
        color: '#64748b',
      }
    },
    MuiPickersDay: {
      day: {
        color: '#334155',
        fontWeight: '500',
        borderRadius: '8px', // beautiful rounded square instead of circle
        '&:hover': {
          backgroundColor: '#f1f5f9',
        }
      },
      daySelected: {
        backgroundColor: '#4f46e5',
        borderRadius: '8px',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#4338ca',
        }
      },
      current: {
        color: '#4f46e5',
        fontWeight: '700',
      }
    },
    MuiPickersModal: {
      dialogRoot: {
        borderRadius: '16px', // modern border radius matching selects
        overflow: 'hidden',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      }
    },
    MuiDialogActions: {
      root: {
        padding: '12px 16px',
        borderTop: '1px solid #e2e8f0',
      }
    }
  }
});

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left'
  },
  title: {
    fontWeight: 'bold',
    flex: '1 0 auto',
    display: 'block',
    marginRight: theme.spacing(2)
  },
  input: {
    fontWeight: 'bold'
  },
  inputElements: {
    display: 'flex',
    gap: '14px'
  }
}));

export default function DateRangePicker({ dateRange: { start, end }, setDateRange }) {
  const intl = useIntl();

  const pickerProps = {
    format: "dd/MM/yyyy",
    animateYearScrolling: true,
    okLabel: <span style={{ color: '#4f46e5', fontWeight: 'bold' }}>{intl.formatMessage(messages.ok)}</span>,
    cancelLabel: <span style={{ color: '#64748b', fontWeight: 'bold' }}>{intl.formatMessage(messages.cancel)}</span>,
    InputProps: {
      disableUnderline: true,
      style: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#334155',
        padding: '0 8px',
        height: '40px',
        textAlign: 'center',
        cursor: 'pointer'
      }
    },
    DialogProps: {
      PaperProps: {
        style: {
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }
      }
    }
  };

  const handleStartChange = date => {
    if (!date) return;
    const newStart = new Date(date).getTime();
    const currentEnd = new Date(end).getTime();
    
    if (newStart > currentEnd) {
      setDateRange({ start: newStart, end: newStart });
    } else {
      setDateRange({ start: newStart, end });
    }
  };

  const handleEndChange = date => {
    if (!date) return;
    const currentStart = new Date(start).getTime();
    const newEnd = new Date(date).getTime();
    
    if (newEnd < currentStart) {
      setDateRange({ start: newEnd, end: newEnd });
    } else {
      setDateRange({ start, end: newEnd });
    }
  };

  return (
    <ThemeProvider theme={datePickerTheme}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%' }}>
        <DatePicker
          {...pickerProps}
          value={start}
          onChange={handleStartChange}
          style={{ width: '110px' }}
        />
        <span style={{ color: '#94a3b8', fontWeight: '500' }}>to</span>
        <DatePicker
          {...pickerProps}
          value={end}
          onChange={handleEndChange}
          style={{ width: '110px' }}
        />
      </div>
    </ThemeProvider>
  );
}
